import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import SEO from '../components/SEO';
import { Label } from '../components/ui/label';
import { MapPin, Phone, Mail, Clock, Facebook, Send, Loader2 } from 'lucide-react';
import { storeInfo } from '../data/mockData';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    honeypot: '' // Bot detection field (hidden)
  });

  const [newsletter, setNewsletter] = useState('');
  const [newsletterHoneypot, setNewsletterHoneypot] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData, {
        headers: { 'Content-Type': 'application/json' }
      });
      
      toast.success(response.data.message || 'Thank you! Your inquiry has been submitted.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '', honeypot: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      
      if (error.response?.status === 429) {
        toast.error('Too many submissions. Please wait a moment and try again.');
      } else if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Please check your input and try again.');
      } else {
        toast.error('Error submitting form. Please try again or contact us directly.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletter) return;
    
    setIsSubscribing(true);
    
    try {
      const response = await axios.post(`${API}/newsletter`, {
        email: newsletter,
        honeypot: newsletterHoneypot
      }, {
        headers: { 'Content-Type': 'application/json' }
      });
      
      toast.success(response.data.message || 'Successfully subscribed to our newsletter!');
      setNewsletter('');
      setNewsletterHoneypot('');
    } catch (error) {
      console.error('Newsletter error:', error);
      
      if (error.response?.status === 429) {
        toast.error('Too many requests. Please wait a moment and try again.');
      } else if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Please enter a valid email address.');
      } else {
        toast.error('Error subscribing. Please try again later.');
      }
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-cyan-50">
      <SEO
        title="Contact Us"
        description="Get in touch with J.B.F. Antiques & Gallery. Inquire about items, request valuations, or ask questions. Call +44 7523 414727 or use our contact form."
        path="/contact"
      />
      {/* Header */}
      <section className="py-16 bg-teal-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-serif font-bold mb-4">
            Contact Us
          </h1>
          <p className="text-xl text-teal-100 max-w-2xl mx-auto">
            We'd love to hear from you. Get in touch to inquire about our antiques or schedule a visit.
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <h2 className="text-3xl font-serif font-bold text-teal-800 mb-8">
              Get In Touch
            </h2>

            <div className="space-y-6 mb-8">
              <Card className="border-teal-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-teal-700" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-teal-800 mb-2">Address</h3>
                      <p className="text-gray-600">{storeInfo.address}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-teal-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="text-teal-700" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-teal-800 mb-2">Phone</h3>
                      <a href={`tel:${storeInfo.phone}`} className="text-gray-600 hover:text-teal-700 transition-colors">
                        {storeInfo.phone}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-teal-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="text-teal-700" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-teal-800 mb-2">Email</h3>
                      <a href={`mailto:${storeInfo.email}`} className="text-gray-600 hover:text-teal-700 transition-colors">
                        {storeInfo.email}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-teal-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="text-teal-700" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-teal-800 mb-2">Opening Hours</h3>
                      <p className="text-gray-600">{storeInfo.openingHours.days}</p>
                      <p className="text-gray-600">{storeInfo.openingHours.hours}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-teal-200 hover:shadow-lg transition-shadow bg-gradient-to-r from-teal-50 to-cyan-100">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-700 rounded-full flex items-center justify-center flex-shrink-0">
                      <Facebook className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-teal-800 mb-2">Follow Us on Facebook</h3>
                      <a 
                        href={storeInfo.facebookUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-teal-700 hover:text-teal-600 transition-colors font-medium"
                      >
                        Visit our Facebook page for latest updates
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <Card className="border-teal-200 shadow-lg">
              <CardContent className="p-6 sm:p-8">
                <h2 className="text-2xl sm:text-3xl font-serif font-bold text-teal-800 mb-6">
                  Send Us a Message
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                  {/* Honeypot field - hidden from users */}
                  <input
                    type="text"
                    name="honeypot"
                    value={formData.honeypot}
                    onChange={handleInputChange}
                    style={{ position: 'absolute', left: '-9999px' }}
                    tabIndex="-1"
                    autoComplete="off"
                    aria-hidden="true"
                  />
                  
                  <div>
                    <Label htmlFor="name" className="text-teal-800 text-base">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      minLength={2}
                      maxLength={100}
                      className="mt-2 border-teal-200 focus:border-teal-700 h-12 text-base"
                      placeholder="Your full name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-teal-800 text-base">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="mt-2 border-teal-200 focus:border-teal-700 h-12 text-base"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-teal-800 text-base">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      maxLength={20}
                      className="mt-2 border-teal-200 focus:border-teal-700 h-12 text-base"
                      placeholder="+44 7xxx xxxxxx"
                    />
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-teal-800 text-base">Subject *</Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      minLength={3}
                      maxLength={200}
                      className="mt-2 border-teal-200 focus:border-teal-700 h-12 text-base"
                      placeholder="What can we help you with?"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-teal-800 text-base">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      minLength={10}
                      maxLength={2000}
                      rows={5}
                      className="mt-2 border-teal-200 focus:border-teal-700 text-base"
                      placeholder="Tell us about your inquiry..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.message.length}/2000 characters
                    </p>
                  </div>

                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-teal-700 hover:bg-teal-600 text-white py-6 text-base sm:text-lg h-auto min-h-[56px] touch-manipulation"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2" size={20} />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Newsletter Signup */}
            <Card className="border-teal-200 shadow-lg mt-8 bg-gradient-to-r from-teal-50 to-cyan-100">
              <CardContent className="p-6 sm:p-8">
                <h3 className="text-xl sm:text-2xl font-serif font-bold text-teal-800 mb-4">
                  Subscribe to Our Newsletter
                </h3>
                <p className="text-gray-700 mb-6 text-sm sm:text-base">
                  Stay updated on new arrivals, special events, and exclusive offers.
                </p>
                <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3">
                  {/* Honeypot field - hidden */}
                  <input
                    type="text"
                    value={newsletterHoneypot}
                    onChange={(e) => setNewsletterHoneypot(e.target.value)}
                    style={{ position: 'absolute', left: '-9999px' }}
                    tabIndex="-1"
                    autoComplete="off"
                    aria-hidden="true"
                  />
                  
                  <Input
                    type="email"
                    value={newsletter}
                    onChange={(e) => setNewsletter(e.target.value)}
                    required
                    className="border-teal-200 focus:border-teal-700 h-12 text-base flex-1"
                    placeholder="Enter your email"
                  />
                  <Button 
                    type="submit"
                    disabled={isSubscribing}
                    className="bg-teal-700 hover:bg-teal-600 text-white h-12 px-6 min-w-[120px] touch-manipulation"
                  >
                    {isSubscribing ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      'Subscribe'
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
